import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_URLS } from '../constants';
import { authenticatedFetch } from '../utils/api';

export interface BillingInfo {
  planName: string;
  billingPeriod: 'monthly' | 'annual';
  nextBillingDate: string;
  amount: number;
  currency: string;
  status: 'active' | 'cancelled' | 'past_due';
}

interface Invoice {
  id: string;
  stripeInvoiceId?: string;
  date: string;
  amount: number;
  currency: string;
  status: 'paid' | 'pending' | 'failed' | 'cancelled';
  invoiceStatus?: 'draft' | 'open' | 'paid' | 'void' | 'uncollectible'; // Stripe status
  description: string;
  hostedInvoiceUrl?: string;
  invoicePdf?: string;
  subtotal?: number;
  tax?: number;
  total?: number;
  attemptCount?: number;
  nextPaymentAttempt?: string;
}

interface PaymentMethod {
  id: string;
  type: 'card' | 'bank';
  last4: string;
  brand?: string;
  expiryMonth?: number;
  expiryYear?: number;
  isDefault: boolean;
}

interface InvoiceSummary {
  total: number;
  paid: number;
  pending: number;
  failed: number;
  voided: number;
  uncollectible: number;
  totalAmount: number;
}

interface Plan {
  id: string;
  name: string;
  monthlyPrice: number;
  annualPrice: number;
  features: string[];
  isPopular?: boolean;
}

const BillingPage = () => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [billingInfo, setBillingInfo] = useState<BillingInfo | null>(null);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoiceSummary, setInvoiceSummary] = useState<InvoiceSummary | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isAnnualBilling, setIsAnnualBilling] = useState(false);
  const [currentView, setCurrentView] = useState<'billing' | 'subscription'>('billing');

  const plans: Plan[] = [
    {
      id: 'basic',
      name: 'Basic',
      monthlyPrice: 10,
      annualPrice: 96,
      features: ['Up to 25 Prompts', 'Basic Analytics', 'Community Access']
    },
    {
      id: 'premium',
      name: 'Premium',
      monthlyPrice: 25,
      annualPrice: 240,
      features: ['Up to 100 Prompts', 'Advanced Analytics', 'Version History', 'Priority Support'],
      isPopular: true
    },
    {
      id: 'unlimited',
      name: 'Unlimited',
      monthlyPrice: 50,
      annualPrice: 480,
      features: ['Unlimited Prompts', 'All Premium Features', 'API Access', 'Team Collaboration']
    }
  ];

  // Load real data from API - Re-execute when user authentication changes
  useEffect(() => {
    // Only load billing data if user is authenticated
    if (!user) {
      setIsLoading(false);
      setBillingInfo(null);
      return;
    }

    const loadBillingData = async (retryCount = 0) => {
      setIsLoading(true);
      try {
        console.log('[BillingPage] Loading billing data for authenticated user:', user.email, '(attempt', retryCount + 1, ')');
        
        // Load subscription data
        const subscriptionResponse = await authenticatedFetch(API_URLS.SUBSCRIPTION.GET_USER_SUBSCRIPTION);
        if (subscriptionResponse.ok) {
          const subscriptionData = await subscriptionResponse.json();
          console.log('[BillingPage] Subscription data loaded:', subscriptionData);
          setBillingInfo({
            planName: subscriptionData.subscription.planName,
            billingPeriod: subscriptionData.subscription.billingPeriod || 'monthly',
            nextBillingDate: subscriptionData.subscription.nextBillingDate,
            amount: subscriptionData.subscription.amount,
            currency: subscriptionData.subscription.currency,
            status: subscriptionData.subscription.status
          });
        } else {
          console.log('[BillingPage] No subscription found, user likely has Free plan');
          // Set default Free plan info when no subscription exists
          setBillingInfo({
            planName: 'Free',
            billingPeriod: 'monthly',
            nextBillingDate: '',
            amount: 0,
            currency: 'USD',
            status: 'active'
          });
        }

        // Load invoices data with all 17 states support
        const invoicesResponse = await authenticatedFetch(API_URLS.SUBSCRIPTION.GET_USER_INVOICES);
        if (invoicesResponse.ok) {
          const invoicesData = await invoicesResponse.json();
          setInvoices(invoicesData.invoices.map((invoice: any) => ({
            id: invoice.id,
            stripeInvoiceId: invoice.stripeInvoiceId,
            date: invoice.billingDate,
            amount: invoice.amount,
            currency: invoice.currency,
            status: invoice.status, // Local status
            invoiceStatus: invoice.invoiceStatus, // Stripe status (17 states)
            description: invoice.description,
            hostedInvoiceUrl: invoice.hostedInvoiceUrl,
            invoicePdf: invoice.invoicePdf,
            subtotal: invoice.subtotal,
            tax: invoice.tax,
            total: invoice.total,
            attemptCount: invoice.attemptCount,
            nextPaymentAttempt: invoice.nextPaymentAttempt
          })));
          
          // Set invoice summary if available
          if (invoicesData.summary) {
            setInvoiceSummary(invoicesData.summary);
          }
        }

        // For now, keep mock payment methods until we implement payment processor integration
        setPaymentMethods([
          {
            id: 'pm_001',
            type: 'card',
            last4: '4242',
            brand: 'Visa',
            expiryMonth: 12,
            expiryYear: 2025,
            isDefault: true
          }
        ]);

        setIsLoading(false);
      } catch (error) {
        console.error('Error loading billing data:', error);
        
        // If we get a 401 error and this is not the 3rd retry attempt,
        // wait briefly and try again (could be an auth race condition)
        if (error instanceof Error && error.message.includes('401') && retryCount < 2) {
          console.log(`[BillingPage] 🔄 401 error detected, retrying in ${800 + (retryCount * 400)}ms (attempt ${retryCount + 2}/3)`);
          // Wait 800ms before retry (increasing with each attempt)
          const delayMs = 800 + (retryCount * 400);
          setTimeout(() => loadBillingData(retryCount + 1), delayMs);
          return;
        }
        
        // Only show error message if all retries have failed
        if (retryCount >= 2) {
          setMessage({ type: 'error', text: 'Failed to load billing information' });
        }
        
        // Set default free plan if we couldn't load subscription data
        if (!billingInfo) {
          setBillingInfo({
            planName: 'Free',
            billingPeriod: 'monthly',
            nextBillingDate: '',
            amount: 0,
            currency: 'USD',
            status: 'active'
          });
        }
        
        setIsLoading(false);
      }
    };

    loadBillingData();
  }, [user]); // Re-execute when user authentication state changes

  // Check URL parameters to determine initial view
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const viewParam = searchParams.get('view');
    if (viewParam === 'subscription') {
      setCurrentView('subscription');
    } else {
      setCurrentView('billing');
    }
  }, [location.search]);

  // This function will be used when Preview Release phase ends
  // @ts-ignore - Will be used after Preview Release
  const handleUpgradePlan = async (planId: string) => {
    try {
      const plan = plans.find(p => p.id === planId);
      if (!plan) {
        setMessage({ type: 'error', text: 'Invalid plan selected' });
        return;
      }

      // Try to get Stripe prices first
      try {
        const pricesResponse = await authenticatedFetch(API_URLS.STRIPE.GET_PRICES);
        if (pricesResponse.ok) {
          const pricesData = await pricesResponse.json();
          const priceId = pricesData.prices[planId]?.[isAnnualBilling ? 'annual' : 'monthly'];
          
          if (priceId) {
            // Create Stripe checkout session
            const checkoutResponse = await authenticatedFetch(API_URLS.STRIPE.CREATE_CHECKOUT_SESSION, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                priceId,
                successUrl: `${window.location.origin}/billing?success=true`,
                cancelUrl: `${window.location.origin}/billing?canceled=true`
              })
            });

            if (checkoutResponse.ok) {
              const checkoutData = await checkoutResponse.json();
              // Redirect to Stripe Checkout
              window.location.href = checkoutData.url;
              return;
            } else {
              const errorData = await checkoutResponse.json();
              setMessage({ type: 'error', text: errorData.message || 'Failed to create checkout session' });
              return;
            }
          }
        }
      } catch (stripeError) {
        console.log('Stripe not available, using mock upgrade');
      }

      // Use our updated Stripe-first endpoint
      const response = await authenticatedFetch(API_URLS.SUBSCRIPTION.MANAGE, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planName: plan.name,
          billingPeriod: isAnnualBilling ? 'annual' : 'monthly'
        })
      });

      if (response.ok) {
        const responseData = await response.json();
        if (responseData.checkoutUrl) {
          // Redirect to Stripe checkout
          window.location.href = responseData.checkoutUrl;
        } else {
          setMessage({ type: 'success', text: responseData.message || `Successfully upgraded to ${plan.name} plan` });
          // Reload billing data
          setTimeout(() => window.location.reload(), 2000);
        }
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to upgrade plan' });
      }
    } catch (error) {
      console.error('Upgrade plan error:', error);
      setMessage({ type: 'error', text: 'Failed to upgrade plan' });
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const response = await authenticatedFetch(API_URLS.SUBSCRIPTION.CANCEL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Subscription cancelled successfully' });
        setShowCancelModal(false);
        if (billingInfo) {
          setBillingInfo({ ...billingInfo, status: 'cancelled' });
        }
        // Reload billing data
        window.location.reload();
      } else {
        const errorData = await response.json();
        setMessage({ type: 'error', text: errorData.message || 'Failed to cancel subscription' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to cancel subscription' });
    }
  };

  const handleManagePaymentDetails = async () => {
    try {
      // Try Stripe portal first
      try {
        const response = await authenticatedFetch(API_URLS.STRIPE.CREATE_PORTAL_SESSION, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            returnUrl: `${window.location.origin}/billing`
          })
        });

        if (response.ok) {
          const data = await response.json();
          // Redirect to Stripe Customer Portal
          window.location.href = data.url;
          return;
        }
      } catch (stripeError) {
        console.log('Stripe portal not available');
      }

      // Fallback message if Stripe is not configured
      setMessage({ 
        type: 'error', 
        text: 'Payment portal not available. Please contact support to manage your payment methods.' 
      });
    } catch (error) {
      console.error('Manage payment details error:', error);
      setMessage({ type: 'error', text: 'Failed to open payment portal' });
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getInvoiceStatusColor = (status: string, invoiceStatus?: string) => {
    // Priority: Show Stripe status first if available, then local status
    const displayStatus = invoiceStatus || status;
    
    switch (displayStatus) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'pending':
      case 'open':
      case 'draft':
        return 'bg-yellow-100 text-yellow-800';
      case 'failed':
      case 'void':
      case 'uncollectible':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-brand-light-gray text-brand-dark-blue';
    }
  };

  const getDisplayStatus = (status: string, invoiceStatus?: string) => {
    const displayStatus = invoiceStatus || status;
    return displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1);
  };

  const handleViewInvoice = (invoice: Invoice) => {
    if (invoice.hostedInvoiceUrl) {
      // Open Stripe hosted invoice in new tab
      window.open(invoice.hostedInvoiceUrl, '_blank');
    } else if (invoice.invoicePdf) {
      // Fallback to PDF if available
      window.open(invoice.invoicePdf, '_blank');
    } else {
      // Show invoice details in a modal or alert
      setMessage({ 
        type: 'error', 
        text: `Invoice ${invoice.id} details not available. Please contact support.` 
      });
    }
  };

  // Admin users see different message
  if (user?.role === 'admin') {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
            <h1 className="text-2xl font-semibold text-gray-900 mb-2">Billing & Subscription</h1>
            <p className="text-gray-600">Manage your subscription, payment methods, and billing history.</p>
          </div>
          <div className="bg-brand-light-gray border border-gray-200 rounded-lg p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-brand-electric-blue" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-brand-dark-blue">Admin Account</h3>
                <p className="text-sm text-brand-dark-blue mt-1">
                  No billing information is applicable for admin users.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
          </div>
        </div>
      </div>
    );
  }

  if (currentView === 'subscription') {
    return (
      <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
            <div className="flex justify-between items-center">
              <h2 className="text-2xl font-semibold text-gray-900">Choose Your Plan</h2>
              <button
                onClick={() => {
                  setCurrentView('billing');
                  navigate('/billing');
                }}
                className="text-brand-electric-blue hover:text-brand-dark-blue font-medium"
              >
                Back to Billing
              </button>
            </div>
          </div>

          {/* Success/Error Message */}
          {message && (
            <div className={`p-4 rounded-md ${
              message.type === 'success'
                ? 'bg-green-50 border border-green-200 text-green-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}>
              {message.text}
            </div>
          )}

          {/* Subscription Plan Selection Page */}
          <div className="bg-gray-100 p-4 sm:p-8 font-sans rounded-lg">
            <div className="max-w-4xl mx-auto">
              {/* Preview Release Banner */}
              <div className="bg-gradient-to-r from-brand-light-gray to-gray-50 border border-gray-200 rounded-xl p-8 mb-8 shadow-sm">
                <div className="text-center">
                  <div className="flex justify-center items-center mb-4">
                    <div className="bg-brand-light-gray rounded-full p-2 mr-3">
                      <svg className="h-5 w-5 text-brand-electric-blue" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <h3 className="text-xl font-bold text-brand-dark-blue">
                      Preview Release
                    </h3>
                  </div>
                  <div className="max-w-4xl mx-auto">
                    <p className="text-brand-dark-blue leading-relaxed text-base">
                      <span className="font-semibold">Welcome to Promptshelf.ai!</span> During this phase, all users have unlimited access to all features. When the Preview Release ends, premium functions and higher usage limits will become available through our subscription plans.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold text-gray-800 mb-2">Choose the Plan That's Right for You</h2>
                <p className="text-gray-600">Start organizing your prompts with a plan that fits your needs.</p>
              </div>
              
              {/* Billing Toggle */}
              <div className="flex justify-center items-center space-x-4 mb-8">
                <span className={`font-medium ${!isAnnualBilling ? 'text-gray-700' : 'text-gray-500'}`}>Monthly</span>
                <label className="flex items-center cursor-pointer">
                  <div className="relative">
                    <input 
                      type="checkbox" 
                      className="sr-only"
                      checked={isAnnualBilling}
                      onChange={(e) => setIsAnnualBilling(e.target.checked)}
                    />
                    <div className="block bg-gray-300 w-14 h-8 rounded-full">
                      <div className={`dot absolute left-1 top-1 bg-white w-6 h-6 rounded-full transition-transform duration-300 ease-in-out ${
                        isAnnualBilling ? 'transform translate-x-6 bg-brand-electric-blue' : ''
                      }`}></div>
                    </div>
                  </div>
                </label>
                <span className={`font-medium ${isAnnualBilling ? 'text-brand-electric-blue' : 'text-gray-500'}`}>
                  Annual (Save 20%)
                </span>
              </div>

              {/* Plan Cards */}
              <div className="grid md:grid-cols-3 gap-8">
                {plans.map((plan) => (
                  <div 
                    key={plan.id}
                    className={`bg-white rounded-lg shadow-md p-6 border flex flex-col relative ${
                      plan.isPopular ? 'border-2 border-brand-electric-blue shadow-lg' : 'border-gray-200'
                    }`}
                  >
                    {plan.isPopular && (
                      <span className="absolute top-0 -translate-y-1/2 bg-brand-electric-blue text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                        Most Popular
                      </span>
                    )}
                    <h3 className={`text-lg font-semibold ${plan.isPopular ? 'text-brand-dark-blue' : 'text-gray-800'}`}>
                      {plan.name}
                    </h3>
                    <p className="text-3xl font-bold my-4">
                      {formatCurrency(isAnnualBilling ? plan.annualPrice : plan.monthlyPrice, 'USD')}
                      <span className="text-base font-normal text-gray-500">
                        /{isAnnualBilling ? 'year' : 'month'}
                      </span>
                    </p>
                    <ul className="space-y-2 text-gray-600 mb-6 flex-grow">
                      {plan.features.map((feature, index) => (
                        <li key={index}>✓ {feature}</li>
                      ))}
                    </ul>
                    <button 
                      disabled
                      className="w-full bg-gray-400 text-white font-semibold py-2 px-4 rounded-lg cursor-not-allowed opacity-60"
                      title="Available after Preview Release ends"
                    >
                      Choose {plan.name}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Billing Management Page (Default View)
  return (
    <div className="min-h-screen bg-gray-50 py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-2">Billing & Subscription</h1>
          <p className="text-gray-600">Manage your subscription, payment methods, and billing history.</p>
        </div>

        {/* Success/Error Message */}
        {message && (
          <div className={`p-4 rounded-md mb-6 ${
            message.type === 'success'
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Current Plan Card */}
        <div className="bg-white rounded-lg shadow-lg p-8 border border-gray-200 mb-6">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Billing & Subscription</h2>
          
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6">
            <h3 className="font-semibold text-gray-700 mb-4">Current Plan</h3>
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-600">Plan</p>
              <p className="font-medium text-gray-800">
                {billingInfo?.planName || 'Free'} {billingInfo?.billingPeriod === 'annual' ? 'Annual' : 'Monthly'}
              </p>
            </div>
            <div className="flex justify-between items-center mb-2">
              <p className="text-gray-600">Status</p>
              <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                billingInfo?.status === 'active' 
                  ? 'text-green-800 bg-green-100' 
                  : billingInfo?.status === 'cancelled'
                  ? 'text-red-800 bg-red-100'
                  : 'text-yellow-800 bg-yellow-100'
              }`}>
                {billingInfo?.status === 'active' ? 'Active' : billingInfo?.status === 'cancelled' ? 'Cancelled' : 'Past Due'}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <p className="text-gray-600">Next payment</p>
              <p className="font-medium text-gray-800">
                {billingInfo?.nextBillingDate 
                  ? `${formatCurrency(billingInfo.amount, billingInfo.currency)} on ${formatDate(billingInfo.nextBillingDate)}`
                  : 'No upcoming payments'
                }
              </p>
            </div>
          </div>

          {/* Management Buttons - Different UI for Free vs Paid Plans */}
          {!billingInfo || billingInfo.planName === 'Free' || !billingInfo.status || billingInfo.status === 'cancelled' ? (
            // Free Plan - Show prominent Upgrade button
            <div>
              <h3 className="font-semibold text-gray-700 mb-4">Upgrade your plan</h3>
              <div className="bg-gradient-to-r from-brand-light-gray to-gray-50 border border-gray-200 rounded-lg p-6 mb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-2">Ready to unlock more features?</h4>
                    <p className="text-gray-600 text-sm mb-4">
                      Upgrade to a paid plan and get access to advanced features, priority support, and more.
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setCurrentView('subscription')}
                  className="w-full bg-gradient-to-r from-brand-dark-blue to-brand-electric-blue text-white font-semibold py-3 px-6 rounded-lg hover:from-brand-electric-blue hover:to-brand-dark-blue transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                >
                  🚀 Upgrade Now
                </button>
              </div>
              
              {/* Secondary actions for free users */}
              <div className="space-y-2">
                <button 
                  onClick={handleManagePaymentDetails}
                  className="w-full text-left bg-white border border-gray-300 text-gray-700 font-medium py-2 px-4 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                >
                  View Billing History
                </button>
              </div>
            </div>
          ) : (
            // Paid Plan - Show standard management options
            <div>
              <h3 className="font-semibold text-gray-700 mb-4">Manage your subscription</h3>
              <div className="space-y-3">
                <button 
                  onClick={() => setCurrentView('subscription')}
                  className="w-full text-left bg-white border border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Change Plan
                </button>
                <button 
                  onClick={handleManagePaymentDetails}
                  className="w-full text-left bg-white border border-gray-300 text-gray-700 font-semibold py-3 px-4 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Manage Payment Details & Invoices
                </button>
                <button 
                  onClick={() => setShowCancelModal(true)}
                  className="w-full text-left text-red-600 font-semibold py-3 px-4 rounded-lg hover:bg-red-50 transition-colors"
                >
                  Cancel Subscription
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Payment Methods */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6 mb-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Payment Methods</h2>
          {paymentMethods.length > 0 ? (
            <div className="space-y-3">
              {paymentMethods.map((method) => (
                <div key={method.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-6 bg-gray-200 rounded flex items-center justify-center">
                      <span className="text-xs font-medium text-gray-600">{method.brand}</span>
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        •••• •••• •••• {method.last4}
                      </div>
                      <div className="text-sm text-gray-500">
                        Expires {method.expiryMonth}/{method.expiryYear}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {method.isDefault && (
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded">
                        Default
                      </span>
                    )}
                    <button className="text-brand-electric-blue hover:text-brand-dark-blue text-sm font-medium">
                      Edit
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500">No payment methods found.</p>
          )}
          <button className="mt-4 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-electric-blue">
            Add Payment Method
          </button>
        </div>

        {/* Billing History */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Billing History</h2>
            {invoiceSummary && (
              <div className="text-sm text-gray-600">
                {invoiceSummary.total} invoice{invoiceSummary.total !== 1 ? 's' : ''} • Total paid: {formatCurrency(invoiceSummary.totalAmount, 'USD')}
              </div>
            )}
          </div>
          
          {/* Invoice Summary Stats */}
          {invoiceSummary && invoiceSummary.total > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{invoiceSummary.paid}</div>
                <div className="text-xs text-gray-500">Paid</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{invoiceSummary.pending}</div>
                <div className="text-xs text-gray-500">Pending</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{invoiceSummary.failed}</div>
                <div className="text-xs text-gray-500">Failed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-600">{invoiceSummary.voided}</div>
                <div className="text-xs text-gray-500">Voided</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{invoiceSummary.uncollectible}</div>
                <div className="text-xs text-gray-500">Uncollectible</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{invoiceSummary.total}</div>
                <div className="text-xs text-gray-500">Total</div>
              </div>
            </div>
          )}
          {invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Description
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(invoice.date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {invoice.description}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatCurrency(invoice.amount, invoice.currency)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${getInvoiceStatusColor(invoice.status, invoice.invoiceStatus)}`}>
                          {getDisplayStatus(invoice.status, invoice.invoiceStatus)}
                        </span>
                        {invoice.attemptCount && invoice.attemptCount > 0 && (
                          <div className="text-xs text-gray-500 mt-1">
                            Attempts: {invoice.attemptCount}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button 
                          onClick={() => handleViewInvoice(invoice)}
                          className="text-brand-electric-blue hover:text-brand-dark-blue"
                          disabled={!invoice.hostedInvoiceUrl && !invoice.invoicePdf}
                        >
                          View
                        </button>
                        {invoice.stripeInvoiceId && (
                          <div className="text-xs text-gray-400 mt-1">
                            {invoice.stripeInvoiceId.substring(0, 8)}...
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-gray-500">No invoices found.</p>
          )}
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Cancel Subscription</h3>
              <p className="text-gray-600 mb-4">
                Your plan remains active until the end of the billing period.
              </p>
              <div className="space-y-2">
                <button
                  onClick={() => setShowCancelModal(false)}
                  className="w-full bg-gray-200 text-gray-800 font-semibold py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                >
                  Keep Plan
                </button>
                <button
                  onClick={handleCancelSubscription}
                  className="w-full bg-red-600 text-white font-semibold py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                >
                  Confirm
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
